import { Pencil, ShieldCheck, Unlock, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useStore } from '../store';

interface ReleaseLeaseModalProps {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Manual escape hatch for the manual_lease_release feature flag. Lets a user
 * drop their own annotate or audit lease — useful when an episode's upstream
 * data fails to load and they want to claim a fresh one. Annotation release is
 * always offered; audit release only when the user has the auditor/admin role.
 */
export function ReleaseLeaseModal({ isOpen, onClose }: ReleaseLeaseModalProps) {
  const { releaseActiveLease, isAuditor, isAdmin } = useStore();
  const [busyKind, setBusyKind] = useState<'annotate' | 'audit' | null>(null);
  const canAudit = isAuditor || isAdmin;

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busyKind) onClose();
    };
    if (isOpen) {
      window.addEventListener('keydown', handleKeyDown);
    }
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose, busyKind]);

  if (!isOpen) return null;

  const handleRelease = async (kind: 'annotate' | 'audit') => {
    if (busyKind) return;
    setBusyKind(kind);
    try {
      await releaseActiveLease(kind);
      onClose();
    } finally {
      setBusyKind(null);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="w-full max-w-md overflow-hidden rounded-2xl border border-brand-border bg-brand-surface shadow-2xl animate-in zoom-in-95 duration-200">
        <div className="flex items-center justify-between border-b border-brand-border px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-dark text-brand-orange">
              <Unlock className="h-5 w-5" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white">Release My Lease</h2>
              <p className="text-xs text-brand-text-secondary">
                Stuck on an episode? Drop it and load a new one.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={busyKind !== null}
            className="rounded-lg p-2 text-brand-text-secondary hover:bg-brand-surface-highlight hover:text-white transition-colors disabled:opacity-40"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-6 space-y-3">
          <p className="text-sm text-brand-text-secondary">
            Releasing a lease frees the episode you currently hold so it can be picked up again. Use
            this only if the episode failed to load or you cannot work on it.
          </p>

          <button
            type="button"
            onClick={() => void handleRelease('annotate')}
            disabled={busyKind !== null}
            className="flex w-full items-center gap-3 rounded-xl border border-brand-border bg-brand-dark/40 px-4 py-3 text-left transition hover:border-brand-orange/50 hover:bg-brand-dark/70 disabled:opacity-50"
          >
            <Pencil className="h-5 w-5 text-brand-text-secondary" />
            <div className="flex-1">
              <div className="text-sm font-semibold text-white">Release annotation lease</div>
              <div className="text-xs text-brand-text-secondary">
                Drop the episode you were annotating (Random).
              </div>
            </div>
            {busyKind === 'annotate' && (
              <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent text-brand-orange" />
            )}
          </button>

          {canAudit && (
            <button
              type="button"
              onClick={() => void handleRelease('audit')}
              disabled={busyKind !== null}
              className="flex w-full items-center gap-3 rounded-xl border border-brand-border bg-brand-dark/40 px-4 py-3 text-left transition hover:border-brand-orange/50 hover:bg-brand-dark/70 disabled:opacity-50"
            >
              <ShieldCheck className="h-5 w-5 text-brand-text-secondary" />
              <div className="flex-1">
                <div className="text-sm font-semibold text-white">Release audit lease</div>
                <div className="text-xs text-brand-text-secondary">
                  Drop the episode you were auditing (Audit).
                </div>
              </div>
              {busyKind === 'audit' && (
                <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent text-brand-orange" />
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
